import { useGet, useMutation } from '../../hooks/useApi';
import { systemApi } from '../../APIs/systemApi';
import {
    Option,
    OptionCause,
    OptionType,
    SystemOptionType,
} from '../../models/System';
import { useEffect, useRef, useState } from 'react';
import { Loading } from '../atomic/effects';
import { Icons } from '../atomic/icons';
import { Button, IconButton } from '../atomic/buttons';
import { CheckBox, ThreeStateCheckbox } from '../atomic/switches';
import { NumberField, TextField } from '../atomic/forms';

const { SYS_FALLBACK, SYS_OVERRIDE, ENV_FALLBACK, ENV_OVERRIDE } = OptionCause;

export const SystemOptions = () => {
    const options = useGet(systemApi.get.options);
    const ref = useRef<HTMLFormElement>(null);

    if (!options.data) {
        return (
            <div className="app-main home">
                <section className="system-settings"></section>
                <Loading />
            </div>
        );
    }
    return (
        <form className="app-main home" ref={ref}>
            <section className="system-settings">
                <div className="system-settings--header">
                    <h2>システム設定</h2>
                    <Button
                        icon={Icons.UpdatePending}
                        disabled={options.isLoading}
                        onClickCapture={(evt) => {
                            evt.preventDefault();
                            options.mutate();
                            return false;
                        }}
                    >
                        更新
                    </Button>
                </div>
                <table>
                    <thead>
                        <tr>
                            <th>名前</th>
                            <td>優先１　環境オーバーライド</td>
                            <td>優先２　システムオーバーライド</td>
                            <td>優先４　システムフォールバック</td>
                            <td>優先５　環境フォールバック</td>
                        </tr>
                    </thead>
                    <tbody>
                        {options.data.data.map(
                            ({ lookup, label, values, type }) => (
                                <tr key={lookup} title={lookup}>
                                    <th>{label}</th>
                                    <td>
                                        <OptionDisplay
                                            value={values[ENV_OVERRIDE]}
                                            type={type}
                                        />
                                    </td>
                                    <td>
                                        <OptionEditorBox
                                            type={SystemOptionType.Override}
                                            lookup={lookup}
                                            value={values[SYS_OVERRIDE]}
                                            inputType={type}
                                        />
                                    </td>
                                    <td>
                                        <OptionEditorBox
                                            type={SystemOptionType.Fallback}
                                            lookup={lookup}
                                            value={values[SYS_FALLBACK]}
                                            inputType={type}
                                        />
                                    </td>
                                    <td>
                                        <OptionDisplay
                                            value={values[ENV_FALLBACK]}
                                            type={type}
                                        />
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
            </section>
            <section className="system-setting-info">
                <table>
                    <thead>
                        <tr>
                            <th>優先度</th>
                            <th>項目名</th>
                            <th>内容</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>1</td>
                            <td>環境オーバーライド</td>
                            <td>
                                環境の状態によってオプションを設定できない場合に使用する
                            </td>
                        </tr>
                        <tr>
                            <td>2</td>
                            <td>システムオーバーライド</td>
                            <td>
                                システム全体に設定される。メンテナンス時など、全テナントの設定を一時的に変更したい場合に利用する
                            </td>
                        </tr>
                        <tr>
                            <td>3</td>
                            <td>テナント設定</td>
                            <td>各テナントに対して設定される</td>
                        </tr>
                        <tr>
                            <td>4</td>
                            <td>システムフォールバック</td>
                            <td>システム全体に設定されるオプションの初期値</td>
                        </tr>
                        <tr>
                            <td>5</td>
                            <td>環境フォールバック</td>
                            <td>他の設定が入っていない時に利用される初期値</td>
                        </tr>
                    </tbody>
                </table>
            </section>
        </form>
    );
};

type OptionEditorProps = {
    type: SystemOptionType;
    value: any;
    inputType: OptionType;
    lookup: string;
};
const OptionEditorBox = (props: OptionEditorProps) => {
    const [state, setState] = useState(Date.now());
    return (
        <div className="editor-box">
            <OptionEditor
                key={state}
                reset={() => {
                    setState(Date.now());
                }}
                {...props}
            />
        </div>
    );
};

const OptionEditor = ({
    inputType,
    type,
    lookup,
    value,
    reset,
}: OptionEditorProps & { reset: () => void }) => {
    const [state, setState] = useState(undefined);
    const update = useMutation(systemApi.mutate.option);
    const changed = state !== undefined && state !== value;
    const _onChange = (input: any) => {
        setState(input);
    };
    useEffect(() => {
        if (value === state) {
            setState(undefined);
        }
    }, [value]);
    const display = state === undefined ? value : state;
    const icons = (
        <>
            {changed ? (
                <div className="change-edit-actions">
                    <IconButton
                        className="no-decoration"
                        disabled={update.isSending}
                        title="保存"
                        icon={Icons.Save}
                        onClickCapture={() => {
                            update.send({
                                option: Option.fromLookup(lookup),
                                type,
                                value: state,
                            });
                        }}
                    />
                    <IconButton
                        className="no-decoration"
                        icon={Icons.Cancel}
                        title="リセット"
                        disabled={update.isSending}
                        onClickCapture={reset}
                    />
                </div>
            ) : null}
        </>
    );
    if (inputType === 'boolean') {
        return (
            <>
                <ThreeStateCheckbox value={value} onChange={_onChange} />
                {icons}
            </>
        );
    }
    const onChange = (text: string) => _onChange(text === '' ? null : text);
    if (inputType === 'int' || inputType === 'uint') {
        return (
            <>
                <NumberField
                    min={inputType === 'uint' ? 0 : undefined}
                    value={display}
                    _onChange={onChange}
                />
                {icons}
            </>
        );
    }
    return (
        <>
            <TextField value={display} _onChange={onChange} />
            {icons}
        </>
    );
};

type OptionDisplayProps = {
    type: OptionType;
    value: any;
};
const OptionDisplay = ({ type, value }: OptionDisplayProps) => {
    if (type === 'boolean') {
        return (
            <CheckBox
                disabled
                checked={value}
                className={value === null ? 'pass-through' : ''}
            />
        );
    }
    if (type === 'int' || type === 'uint') {
        if (value === undefined || value === null) {
            return <>-</>;
        }
        return (
            <>
                <NumberField
                    min={type === 'uint' ? 0 : undefined}
                    disabled
                    value={value}
                />
            </>
        );
    }
    return <>{JSON.stringify(value)}</>;
};
